import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Tooltip from '../../components/Tooltip';

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    start_date: '',
    end_date: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    actions: [],
    entityTypes: [],
  });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
  });

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.offset]);

  const loadFilterOptions = async () => {
    try {
      const response = await api.get('/admin/audit-log/filters');
      setFilterOptions({
        actions: response.data.actions || [],
        entityTypes: response.data.entityTypes || [],
      });
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      query.append('limit', pagination.limit);
      query.append('offset', pagination.offset);
      if (filters.action) query.append('action', filters.action);
      if (filters.entity_type) query.append('entity_type', filters.entity_type);
      if (filters.start_date) query.append('start_date', filters.start_date);
      if (filters.end_date) query.append('end_date', filters.end_date);

      const response = await api.get(`/admin/audit-log?${query.toString()}`);
      setLogs(response.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
      }));
    } catch (err) {
      setError('Failed to load audit log');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      action: '',
      entity_type: '',
      start_date: '',
      end_date: '',
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handlePrevPage = () => {
    setPagination(prev => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  const handleNextPage = () => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getActionColor = (action) => {
    if (action.includes('create')) return 'success';
    if (action.includes('delete') || action.includes('deactivate')) return 'danger';
    if (action.includes('update') || action.includes('activate')) return 'info';
    if (action.includes('login')) return 'warning';
    return 'secondary';
  };

  const formatDetails = (details) => {
    if (!details) return '-';
    try {
      const parsed = typeof details === 'string' ? JSON.parse(details) : details;
      return (
        <div style={styles.details}>
          {Object.entries(parsed).map(([key, value]) => (
            <div key={key}>
              <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </div>
          ))}
        </div>
      );
    } catch {
      return details;
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      <div className="card">
        <h2>Admin Audit Log</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Track all administrative actions performed in the system.
        </p>

        <div style={styles.filterContainer}>
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Action</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                style={styles.filterSelect}
              >
                <option value="">All Actions</option>
                {filterOptions.actions.map(action => (
                  <option key={action} value={action}>{formatAction(action)}</option>
                ))}
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Entity Type</label>
              <select
                value={filters.entity_type}
                onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                style={styles.filterSelect}
              >
                <option value="">All Types</option>
                {filterOptions.entityTypes.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                style={styles.filterInput}
              />
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                style={styles.filterInput}
              />
            </div>

            <button
              className="button secondary"
              onClick={handleClearFilters}
              style={{ alignSelf: 'flex-end' }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading audit log...</div>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Timestamp <Tooltip text="Date and time the action was performed" /></th>
                  <th>User</th>
                  <th>Action <Tooltip text="Type of admin action (Create, Update, Delete, Login)" /></th>
                  <th>Entity <Tooltip text="Type and ID of the object affected" /></th>
                  <th>Details</th>
                  <th>IP Address <Tooltip text="IP address from which the action was performed" /></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: '#666' }}>
                      No audit log entries found.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td style={styles.timestamp}>
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td>
                        {log.user_id ? (
                          <>
                            <div>{log.user_first_name} {log.user_last_name}</div>
                            <div style={styles.email}>{log.user_email}</div>
                          </>
                        ) : (
                          <div style={styles.email}>{log.actor || 'System'}</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${getActionColor(log.action)}`}>
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td>
                        <div>{log.entity_type.replace(/_/g, ' ')}</div>
                        {log.entity_id && (
                          <div style={styles.entityId}>ID: {log.entity_id}</div>
                        )}
                      </td>
                      <td>{formatDetails(log.details)}</td>
                      <td style={styles.ip}>{log.ip_address || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <span>
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div style={styles.paginationButtons}>
                <button
                  className="button secondary"
                  onClick={handlePrevPage}
                  disabled={pagination.offset === 0}
                >
                  Previous
                </button>
                <span style={styles.pageInfo}>
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  className="button secondary"
                  onClick={handleNextPage}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  filterContainer: {
    background: '#f8f9fa',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  filterRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '150px',
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#666',
    marginBottom: '4px',
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
  },
  filterInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
  },
  timestamp: {
    whiteSpace: 'nowrap',
    fontSize: '13px',
  },
  email: {
    fontSize: '12px',
    color: '#666',
  },
  entityId: {
    fontSize: '12px',
    color: '#666',
  },
  details: {
    fontSize: '12px',
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ip: {
    fontFamily: 'monospace',
    fontSize: '12px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
    padding: '10px 0',
    borderTop: '1px solid #eee',
  },
  paginationButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  pageInfo: {
    color: '#666',
    fontSize: '14px',
  },
};

export default AuditLog;
